import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { InputTextComponent } from 'src/app/_shared/components/input-text/input-text.component';
import {
  TranslateModule,
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';
import { CustomerDto, ThemeBaseComponent } from 'src/app/_core';
import { RegisterFormService } from 'src/app/routes/auth/forms/register.form';
import { ModalLoginComponent } from 'src/app/routes/auth/modals/modal-login/modal-login.component';
import { ModalRegisterSummaryComponent } from 'src/app/routes/auth/modals/modal-register-summary/modal-register-summary.component';
import { AuthService } from 'src/app/routes/auth/state/auth.service';
import { environment } from 'src/environments/environment';
import { AccButtonComponent } from 'src/app/_shared/components/acc-button/acc-button.component';
import { IconButtonComponent } from 'src/app/_shared/components/icon-button/icon-button.component';
import { LoaderDirective } from 'src/app/_shared/directives/loader.directive';
import {
  CustomOverlayRef,
  OverlayService,
} from 'src/app/_core/services/overlay.service';
import { InputCheckboxComponent } from 'src/app/_shared/components/input-checkbox/input-checkbox.component';

declare const google: any;
declare const FB: any;
@Component({
  standalone: true,
  selector: 'neopos-modal-register',
  templateUrl: './modal-register.component.html',
  styles: [
    `
      :host {
        display: block;
        width: -webkit-fill-available;
        overflow: hidden;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    LoaderDirective,
    IconButtonComponent,
    InputTextComponent,
    AccButtonComponent,
    InputCheckboxComponent,
    RouterLink,
  ],
  providers: [TranslatePipe],
})
export class ModalRegisterComponent
  extends ThemeBaseComponent
  implements OnDestroy
{
  private readonly _registerFormService = inject(RegisterFormService);
  private readonly _dialogRef = inject(CustomOverlayRef);
  private readonly _overlayService = inject(OverlayService);
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _translateService = inject(TranslateService);
  private readonly _translatePipe = inject(TranslatePipe);
  private readonly _platformId = inject(PLATFORM_ID);

  registerFormVM = this._registerFormService.vm;

  private readonly _mutationCreateCustomer = this._authService.createCustomer();
  private _mutationGoogleLogin = this._authService.googleLogin();
  private _mutationFacebookLogin = this._authService.facebookLogin();

  mutationCreateCustomer = toSignal(this._mutationCreateCustomer.result$);
  mutationGoogleLogin = toSignal(this._mutationGoogleLogin.result$);
  mutationFacebookLogin = toSignal(this._mutationFacebookLogin.result$);

  ngOnDestroy(): void {
    this._registerFormService.resetForm();
  }

  onWindowOpen(url: string) {
    if (isPlatformBrowser(this._platformId)) {
      window.open(this._translatePipe.transform(url), '_blank');
    }
  }

  close = (): void => this._dialogRef.close();

  onCreateCustomer(): void {
    if (this._registerFormService.canSubmitForm()) {
      this._mutationCreateCustomer
        .mutate(this._prepareRegisterData())
        .then(() => {
          this._registerFormService.resetForm();

          this._overlayService.open(
            {
              width: 434,
              hasBackdrop: true,
              backdropClass: 'backdrop-style',
              responsive: true,
            },
            ModalRegisterSummaryComponent
          );
        });
    }
  }

  onOpenLoginModal(): void {
    this.close();

    this._overlayService.open(
      {
        width: 434,
        hasBackdrop: true,
        backdropClass: 'backdrop-style',
        responsive: true,
      },
      ModalLoginComponent
    );
  }

  onGoogleLogin(): void {
    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (response: any) => {
        if (response.credential) {
          this._mutationGoogleLogin.mutate(response.credential).then(() => {
            this._router.navigate([this._translateService.instant('/konto')]);
            this.close();
          });
        }
      },
    });

    google.accounts.id.prompt();
  }

  onFacebookLogin(): void {
    FB.init({
      appId: environment.facebookAppId,
      cookie: true,
      xfbml: false,
      version: 'v19.0',
    });

    FB.login(
      (response: any) => {
        if (response.authResponse) {
          const accessToken = response.authResponse.accessToken;
          this._mutationFacebookLogin.mutate(accessToken).then(() => {
            this._router.navigate([this._translateService.instant('/konto')]);
            this.close();
          });
        } else {
          console.error('Facebook login cancelled or failed', response);
        }
      },
      { scope: 'email,public_profile' }
    );
  }

  private _prepareRegisterData(): CustomerDto {
    const { firstName, lastName, email, phone, password } =
      this.registerFormVM.getRawValue();

    return {
      firstName,
      lastName,
      email,
      phoneNumber: phone,
      password: password!,
    };
  }
}
